import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';

export function useNotifications(userId) {
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();
  const realtimeFetchTimerRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!userId) {
      setNotifications([]);
      return;
    }
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(30);
    if (error) {
      console.error('Failed to fetch notifications:', error);
      return;
    }
    const rows = data || [];

    // An actor can now be either a veteran (profiles) or an employer
    // (employer_profiles) -- notifications.actor_id no longer FKs to a
    // single table, so PostgREST's embed shortcut can't resolve it.
    // Look up both and merge, tagging which kind each actor is so
    // getLabel can render a name or a company name correctly.
    const actorIds = [...new Set(rows.map(n => n.actor_id))];
    const actorMap = {};
    if (actorIds.length > 0) {
      const [{ data: vetActors }, { data: empActors }] = await Promise.all([
        supabase.from('profiles_public').select('id, first_name, last_name, avatar_url').in('id', actorIds),
        supabase.from('employer_profiles_public').select('id, company_name, logo_url').in('id', actorIds),
      ]);
      (vetActors || []).forEach(a => {
        actorMap[a.id] = { kind: 'veteran', first_name: a.first_name, last_name: a.last_name, avatar_url: a.avatar_url };
      });
      (empActors || []).forEach(a => {
        actorMap[a.id] = { kind: 'employer', company_name: a.company_name, avatar_url: a.logo_url };
      });
    }

    setNotifications(rows.map(n => ({ ...n, actor: actorMap[n.actor_id] || null })));
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      return;
    }

    // Fetch once immediately on mount
    fetchNotifications();

    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        (payload) => {
          console.log('Realtime notification received:', payload);
          if (realtimeFetchTimerRef.current) clearTimeout(realtimeFetchTimerRef.current);
          realtimeFetchTimerRef.current = setTimeout(() => {
            fetchNotifications();
            realtimeFetchTimerRef.current = null;
          }, 250);
        }
      )
      .subscribe((status) => {
        console.log('Notification subscription status:', status);
      });

    return () => {
      if (realtimeFetchTimerRef.current) {
        clearTimeout(realtimeFetchTimerRef.current);
        realtimeFetchTimerRef.current = null;
      }
      supabase.removeChannel(channel);
    };
  }, [userId, fetchNotifications]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAllRead = useCallback(async () => {
    if (!userId) return;
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', userId)
      .eq('read', false);
    if (error) {
      console.error('Failed to mark all notifications read:', error);
      return;
    }
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, [userId]);

  const markOneRead = useCallback(async (id) => {
    if (!userId) return;
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', id)
      .eq('user_id', userId);
    if (error) {
      console.error('Failed to mark notification read:', error);
      return;
    }
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  }, [userId]);

  const handleClick = useCallback(async (notif) => {
    await markOneRead(notif.id);
    if (notif.type === 'connection_request' || notif.type === 'connection_accepted' || notif.type === 'new_follow') {
      navigate('/network');
    } else if (notif.type === 'new_message') {
      navigate('/messages');
    } else if (notif.type === 'new_application') {
      navigate('/employer/dashboard');
    } else if (notif.type === 'application_status_change') {
      navigate('/jobs');
    } else if (notif.type === 'forum_reply') {
      navigate(`/forums/${notif.entity_id}`);
    }
  }, [markOneRead, navigate]);

  function getLabel(notif) {
    const actor = notif.actor;
    const name = actor?.kind === 'employer'
      ? (actor.company_name || 'An employer')
      : ([actor?.first_name, actor?.last_name].filter(Boolean).join(' ').trim() || 'Someone');
    switch (notif.type) {
      case 'connection_request':        return `${name} sent you a connection request`;
      case 'connection_accepted':       return `${name} accepted your connection`;
      case 'new_message':               return `${name} sent you a message`;
      case 'new_follow':                return `${name} started following you`;
      case 'new_application':           return `${name} applied to your job posting`;
      case 'application_status_change': return `${name} updated the status of your application`;
      case 'forum_reply':               return `${name} replied to your forum post`;
      default:                          return 'New notification';
    }
  }

  // A glowing dot instead of an emoji glyph -- color groups notifications
  // by category (network, messaging, forum, hiring pipeline) rather than
  // relying on emoji, which render inconsistently across fonts/platforms.
  function getColor(type) {
    switch (type) {
      case 'connection_request':        return '#C9A84C'; // gold — network
      case 'connection_accepted':       return '#68d391'; // green — positive outcome
      case 'new_follow':                return '#C9A84C'; // gold — network
      case 'new_message':               return '#63b3ed'; // blue — messaging
      case 'forum_reply':               return '#a78bfa'; // purple — forum/community
      case 'new_application':           return '#f6ad55'; // orange — hiring pipeline
      case 'application_status_change': return '#f6ad55'; // orange — hiring pipeline
      default:                          return '#8FA3BE'; // muted — fallback
    }
  }

  function timeAgo(dateStr) {
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  }

  return { notifications, unreadCount, handleClick, markAllRead, getLabel, getColor, timeAgo };
}
